import { useState } from 'react';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { ProgressSpinner } from 'primereact/progressspinner';
import { toLocalDateTime } from '../../utils/date-helper';
import {
  useDeactivateSupportPortal,
  useLinearCustomerMapping,
  useReactivateSupportPortal,
  useSetSupportPortalCustomer,
  useUnassignedLinearCustomers,
} from '../../hooks/useLinearCustomerMapping';
import { LinearMappingStatus } from '../../models/linearCustomerMapping';

const statusSeverity = (status: LinearMappingStatus) => {
  switch (status) {
    case 'Active':
      return 'success' as const;
    case 'Inactive':
      return 'danger' as const;
    default:
      return 'warning' as const;
  }
};

const SupportPortalConfig = (props: { tenantId?: string }): JSX.Element => {
  const { tenantId } = props;
  const [deactivationReason, setDeactivationReason] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);

  const { data: mapping, isLoading, isError, error } = useLinearCustomerMapping(tenantId);
  const deactivate = useDeactivateSupportPortal(tenantId);
  const reactivate = useReactivateSupportPortal(tenantId);
  const setCustomer = useSetSupportPortalCustomer(tenantId);

  const status: LinearMappingStatus = mapping?.status ?? 'SetupIncomplete';
  const unassignedCustomers = useUnassignedLinearCustomers(!!tenantId && status === 'SetupIncomplete');

  const mutationError = deactivate.error || reactivate.error || setCustomer.error;
  const isMutating = deactivate.isPending || reactivate.isPending || setCustomer.isPending;

  if (!tenantId) {
    return (
      <div className="text-red-600 text-sm py-4">
        Unable to determine the current tenant. Reload the page and try again.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <ProgressSpinner style={{ width: '2rem', height: '2rem' }} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-red-600 text-sm py-4">
        {error instanceof Error ? error.message : 'Failed to load support configuration.'}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <span className="text-gray-600 font-medium">Support portal</span>
        <Tag value={status} severity={statusSeverity(status)} />
      </div>

      {mapping?.linearCustomerId && (
        <div className="text-gray-500 text-sm">Customer ID: {mapping.linearCustomerId}</div>
      )}
      {mapping?.lastValidatedAt && (
        <div className="text-gray-500 text-sm">
          Last validated: {toLocalDateTime(mapping.lastValidatedAt)}
        </div>
      )}
      {status !== 'Active' && mapping?.failureReason && (
        <div className="text-amber-600 text-sm">{mapping.failureReason}</div>
      )}

      {status === 'Active' && (
        <>
          <div className="text-gray-500 text-sm">Support is enabled for this organization.</div>
          <InputText
            value={deactivationReason}
            onChange={(event) => setDeactivationReason(event.target.value)}
            placeholder="Reason (optional)"
            className="w-full"
          />
          <div>
            <Button
              label="Disable support"
              icon="pi pi-ban"
              severity="danger"
              outlined
              loading={deactivate.isPending}
              disabled={isMutating}
              onClick={() => deactivate.mutate({ reason: deactivationReason.trim() || undefined })}
            />
          </div>
        </>
      )}

      {status === 'Inactive' && (
        <>
          {mapping?.deactivationReason && (
            <div className="text-gray-500 text-sm">Reason: {mapping.deactivationReason}</div>
          )}
          {mapping?.deactivatedAt && (
            <div className="text-gray-500 text-sm">
              Disabled: {toLocalDateTime(mapping.deactivatedAt)}
            </div>
          )}
          <div>
            <Button
              label="Re-enable support"
              icon="pi pi-refresh"
              loading={reactivate.isPending}
              disabled={isMutating}
              onClick={() => reactivate.mutate()}
            />
          </div>
        </>
      )}

      {status === 'SetupIncomplete' && (
        <>
          <div className="text-gray-500 text-sm">
            Connect this organization to its Linear Customer to enable the support portal. Only
            Customers not yet linked to a tenant are listed. Create one in Linear and refresh if it
            is missing.
          </div>
          {unassignedCustomers.isLoading ? (
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <i className="pi pi-spin pi-spinner" />
              <span>Loading Linear Customers&hellip;</span>
            </div>
          ) : unassignedCustomers.isError ? (
            <div className="text-red-600 text-sm">Failed to load Linear Customers.</div>
          ) : (
            <div className="flex gap-2">
              <Dropdown
                value={selectedCustomerId}
                options={unassignedCustomers.data ?? []}
                optionLabel="name"
                optionValue="id"
                filter
                placeholder="Select a Linear Customer"
                emptyMessage="No unassigned Linear Customers"
                className="flex-1"
                disabled={isMutating}
                onChange={(event) => setSelectedCustomerId(event.value)}
              />
              <Button
                label="Connect"
                icon="pi pi-link"
                loading={setCustomer.isPending}
                disabled={isMutating || !selectedCustomerId}
                onClick={() => selectedCustomerId && setCustomer.mutate(selectedCustomerId)}
              />
            </div>
          )}
        </>
      )}

      {mutationError && (
        <div className="text-red-600 text-sm">
          {mutationError instanceof Error ? mutationError.message : 'Action failed. Please retry.'}
        </div>
      )}
    </div>
  );
};

export default SupportPortalConfig;
